import { SkillCategory } from '@applicator/shared'
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'

import SkillCategoryItem from './SkillCategoryItem'
import SkillsMultiSelectBar from './SkillsMultiSelectBar'
import SkillsInclusionBanner from './SkillsInclusionBanner'
import ResumeSectionEmptyHint from './ResumeSectionEmptyHint'
import type { TemplateResumeSummary } from '../../lib/resumeTemplates'
import { sortSkillsInCategories } from './skillsEditorUtils'
import { useActiveSkillCategoryScroll } from './useActiveSkillCategoryScroll'
import { reorderItemsById } from './useWorkExperienceRoleHandlers/reorderHelpers'

const DRAG_DATA_TYPE = 'text/plain'

export default function SkillsEditor({
  data,
  onChange,
  scrollRootRef,
  onActiveCategoryChange,
  isTemplateResume = false,
  templateResumes = [],
  fetchTemplateSkillsForResume,
}: {
  data: SkillCategory[]
  onChange: (data: SkillCategory[]) => void
  scrollRootRef?: React.RefObject<HTMLElement | null>
  onActiveCategoryChange?: (categoryName: string | null) => void
  isTemplateResume?: boolean
  templateResumes?: TemplateResumeSummary[]
  fetchTemplateSkillsForResume?: (templateResumeId: string) => Promise<SkillCategory[] | null>
}) {
  const categories = useMemo(
    () => sortSkillsInCategories(Array.isArray(data) ? data : []),
    [data]
  )
  const containerRef = useRef<HTMLDivElement>(null)
  const emitChange = useCallback(
    (next: SkillCategory[]) => onChange(sortSkillsInCategories(next)),
    [onChange]
  )

  const { activeCategoryName } = useActiveSkillCategoryScroll(categories, scrollRootRef, containerRef)

  useEffect(() => {
    onActiveCategoryChange?.(activeCategoryName)
    return () => onActiveCategoryChange?.(null)
  }, [activeCategoryName, onActiveCategoryChange])
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null)
  const [editingCategoryName, setEditingCategoryName] = useState('')
  const [editingSkillId, setEditingSkillId] = useState<{ categoryId: string; skillId: string } | null>(null)
  const [editingSkillName, setEditingSkillName] = useState('')
  const [newSkillInput, setNewSkillInput] = useState<{ categoryId: string; value: string } | null>(null)
  const [selectedSkills, setSelectedSkills] = useState<Set<string>>(new Set()) // Set of "categoryId-skillId"
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false)
  const [draggedSkillId, setDraggedSkillId] = useState<string | null>(null)
  const [draggedCategoryId, setDraggedCategoryId] = useState<string | null>(null)
  const [dragOverCategoryId, setDragOverCategoryId] = useState<string | null>(null)
  const [draggedCategoryBlockId, setDraggedCategoryBlockId] = useState<string | null>(null)
  const [dragOverCategoryIndex, setDragOverCategoryIndex] = useState<number | null>(null)
  const [moveToCategoryMenu, setMoveToCategoryMenu] = useState<boolean>(false)
  const moveMenuRef = useRef<HTMLDivElement>(null)
  const draggedCategoryBlockIdRef = useRef<string | null>(null)

  // Close menus on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (moveMenuRef.current && !moveMenuRef.current.contains(event.target as Node)) {
        setMoveToCategoryMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleAddCategory = () => {
    const newCategory: SkillCategory = {
      id: `category-${Date.now()}`,
      name: '',
      skills: [],
    }
    emitChange([...categories, newCategory])
    setTimeout(() => {
      setEditingCategoryId(newCategory.id)
      setEditingCategoryName('')
    }, 0)
  }

  const handleSaveCategory = (categoryId: string) => {
    const updated = categories.map((cat) =>
      cat.id === categoryId ? { ...cat, name: editingCategoryName.trim() || 'New Category' } : cat
    )
    emitChange(updated)
    setEditingCategoryId(null)
  }

  const handleDeleteCategory = (categoryId: string) => {
    emitChange(categories.filter((cat) => cat.id !== categoryId))
  }

  const handleAddSkill = (categoryId: string) => {
    if (newSkillInput?.categoryId === categoryId && newSkillInput.value.trim()) {
      // Split by comma and create multiple skills
      const skillNames = newSkillInput.value
        .split(',')
        .map(name => name.trim())
        .filter(name => name.length > 0)
      
      if (skillNames.length > 0) {
        const updated = categories.map((cat) => {
          if (cat.id === categoryId) {
            const newSkills = skillNames.map((skillName, index) => ({
              id: `skill-${Date.now()}-${index}-${crypto.randomUUID()}`,
              name: skillName,
              included: true,
            }))
            return { ...cat, skills: [...(cat.skills || []), ...newSkills] }
          }
          return cat
        })
        emitChange(updated)
        setNewSkillInput(null)
      } else {
        setNewSkillInput(null)
      }
    } else {
      setNewSkillInput({ categoryId, value: '' })
    }
  }

  const handleDeleteSkill = (categoryId: string, skillId: string) => {
    const updated = categories.map((cat) => {
      if (cat.id === categoryId) {
        return { ...cat, skills: (cat.skills || []).filter((s) => s.id !== skillId) }
      }
      return cat
    })
    emitChange(updated)
    // Remove from selection if selected
    const skillKey = `${categoryId}-${skillId}`
    if (selectedSkills.has(skillKey)) {
      setSelectedSkills((prev) => {
        const next = new Set(prev)
        next.delete(skillKey)
        return next
      })
    }
  }

  const handleToggleSkill = (categoryId: string, skillId: string) => {
    const updated = categories.map((cat) => {
      if (cat.id === categoryId) {
        return {
          ...cat,
          skills: (cat.skills || []).map((skill) =>
            skill.id === skillId ? { ...skill, included: !skill.included } : skill
          ),
        }
      }
      return cat
    })
    emitChange(updated)
  }

  const handleToggleSkillSelection = (categoryId: string, skillId: string, e: React.MouseEvent) => {
    if (isMultiSelectMode || e.ctrlKey || e.metaKey) {
      e.stopPropagation()
      const skillKey = `${categoryId}-${skillId}`
      setSelectedSkills((prev) => {
        const next = new Set(prev)
        if (next.has(skillKey)) {
          next.delete(skillKey)
        } else {
          next.add(skillKey)
        }
        return next
      })
      if (!isMultiSelectMode) {
        setIsMultiSelectMode(true)
      }
    }
  }

  const handleMoveSelectedSkills = (targetCategoryId: string) => {
    if (selectedSkills.size === 0) return

    const updated = categories.map((cat) => {
      const skillsToKeep: Array<{ id: string; name: string; included: boolean }> = []
      const skillsToExtract: Array<{ id: string; name: string; included: boolean }> = []

      ;(cat.skills || []).forEach((skill) => {
        const skillKey = `${cat.id}-${skill.id}`
        if (selectedSkills.has(skillKey)) {
          skillsToExtract.push(skill)
        } else {
          skillsToKeep.push(skill)
        }
      })

      if (cat.id === targetCategoryId) {
        // Add moved skills to target category
        return { ...cat, skills: [...skillsToKeep, ...skillsToExtract] }
      } else {
        // Remove moved skills from source category
        return { ...cat, skills: skillsToKeep }
      }
    })

    emitChange(updated)
    setSelectedSkills(new Set())
    setIsMultiSelectMode(false)
    setMoveToCategoryMenu(false)
  }

  const clearCategoryBlockDrag = () => {
    draggedCategoryBlockIdRef.current = null
    setDraggedCategoryBlockId(null)
    setDragOverCategoryIndex(null)
  }

  const handleReorderCategories = (draggedId: string, targetIndex: number) => {
    emitChange(reorderItemsById(categories, draggedId, targetIndex))
  }

  const handleCategoryBlockDragStart = (categoryId: string, e: React.DragEvent) => {
    draggedCategoryBlockIdRef.current = categoryId
    setDraggedCategoryBlockId(categoryId)
    setDraggedSkillId(null)
    setDraggedCategoryId(null)
    setDragOverCategoryId(null)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData(
      DRAG_DATA_TYPE,
      JSON.stringify({ type: 'category-block', categoryId })
    )
  }

  const handleCategoryBlockDragOver = (categoryIndex: number, e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (draggedCategoryBlockIdRef.current) {
      setDragOverCategoryIndex(categoryIndex)
    }
  }

  const handleCategoryBlockDrop = (categoryIndex: number, e: React.DragEvent) => {
    e.preventDefault()
    try {
      const dragData = JSON.parse(e.dataTransfer.getData(DRAG_DATA_TYPE))
      if (dragData.type === 'category-block' && dragData.categoryId) {
        handleReorderCategories(dragData.categoryId, categoryIndex)
      }
    } catch {
      // ignore malformed drag payload
    }
    clearCategoryBlockDrag()
  }

  const handleDragStart = (categoryId: string, skillId: string, e: React.DragEvent) => {
    clearCategoryBlockDrag()
    setDraggedSkillId(skillId)
    setDraggedCategoryId(categoryId)
    e.dataTransfer.effectAllowed = 'move'
    // If multiple skills selected, include them in drag
    if (selectedSkills.size > 1) {
      const skillKey = `${categoryId}-${skillId}`
      if (selectedSkills.has(skillKey)) {
        // Drag all selected skills
        e.dataTransfer.setData(DRAG_DATA_TYPE, JSON.stringify({ 
          type: 'multiple',
          skillKeys: Array.from(selectedSkills)
        }))
      } else {
        e.dataTransfer.setData(DRAG_DATA_TYPE, JSON.stringify({ 
          type: 'single',
          categoryId,
          skillId
        }))
      }
    } else {
      e.dataTransfer.setData('text/plain', JSON.stringify({ 
        type: 'single',
        categoryId,
        skillId
      }))
    }
  }

  const handleDragOver = (categoryId: string, e: React.DragEvent) => {
    if (draggedCategoryBlockIdRef.current) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOverCategoryId(categoryId)
  }

  const handleDrop = (targetCategoryId: string, e: React.DragEvent) => {
    e.preventDefault()
    try {
      const dragData = JSON.parse(e.dataTransfer.getData(DRAG_DATA_TYPE))
      if (dragData.type === 'category-block') {
        return
      }
      
      if (dragData.type === 'multiple') {
        // Move all selected skills
    const updated = categories.map((cat) => {
      const skillsToKeep: Array<{ id: string; name: string; included: boolean }> = []
      const skillsToExtract: Array<{ id: string; name: string; included: boolean }> = []

          ;(cat.skills || []).forEach((skill) => {
            const skillKey = `${cat.id}-${skill.id}`
            if (dragData.skillKeys.includes(skillKey)) {
              skillsToExtract.push(skill)
            } else {
              skillsToKeep.push(skill)
            }
          })

          if (cat.id === targetCategoryId) {
            return { ...cat, skills: [...skillsToKeep, ...skillsToExtract] }
          } else {
            return { ...cat, skills: skillsToKeep }
          }
        })
        emitChange(updated)
        setSelectedSkills(new Set())
        setIsMultiSelectMode(false)
      } else {
        // Move single skill
        const sourceCategoryId = dragData.categoryId
        const skillId = dragData.skillId
        
        if (sourceCategoryId === targetCategoryId) return

        const updated = categories.map((cat) => {
          if (cat.id === sourceCategoryId) {
            return {
              ...cat,
              skills: (cat.skills || []).filter((s) => s.id !== skillId),
            }
          }
          if (cat.id === targetCategoryId) {
            const skillToMove = categories
              .find((c) => c.id === sourceCategoryId)
              ?.skills?.find((s) => s.id === skillId)
            if (skillToMove) {
              return { ...cat, skills: [...(cat.skills || []), skillToMove] }
            }
          }
          return cat
        })
        emitChange(updated)
      }
    } catch (err) {
      // Error handled silently
    }
    setDraggedSkillId(null)
    setDraggedCategoryId(null)
    setDragOverCategoryId(null)
  }

  const handleDragEnd = () => {
    setDraggedSkillId(null)
    setDraggedCategoryId(null)
    setDragOverCategoryId(null)
    clearCategoryBlockDrag()
  }


  const handleCreateNewCategoryAndMove = () => {
    const newCategory: SkillCategory = {
      id: `category-${Date.now()}`,
      name: 'New Category',
      skills: [],
    }
    const updated = [...categories, newCategory]
    emitChange(updated)
    setTimeout(() => {
      handleMoveSelectedSkills(newCategory.id)
      setEditingCategoryId(newCategory.id)
      setEditingCategoryName('')
    }, 0)
  }

  // Prepare props for SkillCategoryItem
  const categoryItemProps = {
    categories,
    editingCategoryId,
    editingCategoryName,
    editingSkillId,
    editingSkillName,
    newSkillInput,
    selectedSkills,
    isMultiSelectMode,
    draggedSkillId,
    draggedCategoryId,
    dragOverCategoryId,
    draggedCategoryBlockId,
    dragOverCategoryIndex,
    isCategoryBlockDragActive: () => draggedCategoryBlockIdRef.current !== null,
    onChange: emitChange,
    onStartEditCategory: (categoryId: string) => {
      setEditingCategoryId(categoryId)
      setEditingCategoryName(categories.find(c => c.id === categoryId)?.name || '')
    },
    onSaveCategory: handleSaveCategory,
    onDeleteCategory: handleDeleteCategory,
    onAddSkill: handleAddSkill,
    onDeleteSkill: handleDeleteSkill,
    onToggleSkill: handleToggleSkill,
    onToggleSkillSelection: handleToggleSkillSelection,
    onDragStart: handleDragStart,
    onDragEnd: handleDragEnd,
    onDragOver: handleDragOver,
    onDrop: handleDrop,
    onDragLeave: (categoryIndex: number) => {
      setDragOverCategoryId(null)
      if (dragOverCategoryIndex === categoryIndex) {
        setDragOverCategoryIndex(null)
      }
    },
    onCategoryBlockDragStart: handleCategoryBlockDragStart,
    onCategoryBlockDragOver: handleCategoryBlockDragOver,
    onCategoryBlockDrop: handleCategoryBlockDrop,
    onCategoryBlockDragEnd: clearCategoryBlockDrag,
    setEditingCategoryId,
    setEditingCategoryName,
    setEditingSkillId,
    setEditingSkillName,
    setNewSkillInput,
  }

  return (
    <div ref={containerRef} className="space-y-4">
      <SkillsInclusionBanner
        categories={categories}
        isTemplateResume={isTemplateResume}
        templateResumes={templateResumes}
        fetchTemplateSkillsForResume={
          fetchTemplateSkillsForResume ?? (async () => null)
        }
        onApply={emitChange}
      />

      <SkillsMultiSelectBar
        selectedCount={selectedSkills.size}
        categories={categories}
        moveToCategoryMenu={moveToCategoryMenu}
        moveMenuRef={moveMenuRef}
        onToggleMoveMenu={() => setMoveToCategoryMenu(!moveToCategoryMenu)}
        onMoveToCategory={handleMoveSelectedSkills}
        onCreateNewCategoryAndMove={handleCreateNewCategoryAndMove}
        onCancel={() => {
          setSelectedSkills(new Set())
          setIsMultiSelectMode(false)
        }}
      />

      {categories.length === 0 && <ResumeSectionEmptyHint sectionType="skills" />}

      {categories.map((category, categoryIndex) => (
        <SkillCategoryItem
          key={category.id}
          category={category}
          categoryIndex={categoryIndex}
          {...categoryItemProps}
        />
      ))}

      <button
        onClick={handleAddCategory}
        className="text-sm text-interactive hover:text-interactive font-medium"
      >
        + Add Group
      </button>
    </div>
  )
}
